/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 02/09/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import '../view-audit/viewAuditFilterForm.css'

/**
 * HOC for the partner form
 */
class ViewErrorFilterForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            outcomesErrorFromDate: '',
            outcomesErrorToDate: '',

            outComesErrorPartnerId: '',
            outComesErrorPartnerName: '',
            outComesErrorInstitutionId: '',
            outComesErrorInstitutionName: '',
            outComesErrorDataSource: '',
            outComesErrorMessageType: '',
            outComesErrorStatus: '',
            outComesErrorCourseId: '',
            outComesErrorError: '',
            outComesErrorTransactionId: '',

            errors: {},

            // form fields
            fields: [
                {
                    inputType: 'dropdown',
                    labelName: 'Partner',
                    inputName: 'partnerId',
                    optionValueName: 'outComesErrorPartnerId',
                    optionLabelName: 'outComesErrorPartnerName',
                    required: true,
                    path: '/view-audit',
                    valueType: 'gradeBookPartners'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Institution',
                    inputName: 'outComesErrorInstitutionName',
                    optionValueName: 'outComesErrorInstitutionId',
                    optionLabelName: 'outComesErrorInstitutionName',
                    required: false,
                    path: '/view-error',
                    valueType: 'gradeBookInstitutions'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Platform',
                    inputName: 'outComesErrorDataSource',
                    optionValueName: 'outComesErrorDataSource',
                    optionLabelName: 'outComesErrorDataSource',
                    required: false,
                    path: '/view-error',
                    valueType: 'platforms'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Type',
                    inputName: 'outComesErrorMessageType',
                    optionValueName: 'outComesErrorMessageType',
                    optionLabelName: 'outComesErrorMessageType',
                    required: false,
                    path: '',
                    valueType: 'view-error-type'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Status',
                    inputName: 'outComesErrorStatus',
                    optionValueName: 'outComesErrorStatus',
                    optionLabelName: 'outComesErrorStatus',
                    required: false,
                    path: '',
                    valueType: 'view-error-status'
                },
                {
                    inputType: 'text',
                    labelName: 'CourseId',
                    inputName: 'outComesErrorCourseId',
                    optionValueName: 'outComesErrorCourseId',
                    optionLabelName: 'outComesErrorCourseId',
                    placeholder: 'Please enter Course ID here',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'Error/Action',
                    inputName: 'outComesErrorError',
                    optionValueName: 'outComesErrorError',
                    optionLabelName: 'outComesErrorError',
                    placeholder: 'Please enter error/action here',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'Transaction Id',
                    inputName: 'outComesErrorTransactionId',
                    optionValueName: 'outComesErrorTransactionId',
                    optionLabelName: 'outComesErrorTransactionId',
                    placeholder: 'Please enter transaction ID here',
                    required: false
                },
                {
                    inputType: 'dateTime',
                    labelName: 'From',
                    inputName: 'outcomesErrorFromDate',
                    optionValueName: 'outcomesErrorFromDate',
                    optionLabelName: 'outcomesErrorFromDate',
                    valueType: 'startDate'
                },
                {
                    inputType: 'dateTime',
                    labelName: 'To',
                    inputName: 'outcomesErrorToDate',
                    optionValueName: 'outcomesErrorToDate',
                    optionLabelName: 'outcomesErrorToDate',
                    valueType: 'endDate'
                }
            ]
        };
        EventEmitter.emit('setPageHeaderData', {
            page: 'view-error',
            pageName: "View Errors",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/view-error",
            optionalIcons: ['manageColumnsIcon']
        });
    }

    /**
     * update the state when user inout a value
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionLabelName]: event.label,
                [event.optionValueName]: event.value
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            });
        }
    };

    loadFilteredResults = () => {
        let search = '&search=%7B'

        // get required fields
        for (const [key, value] of Object.entries(this.state)) {
            switch (key) {
                case 'outComesErrorPartnerName':
                case 'outComesErrorInstitutionName':
                case 'outComesErrorDataSource':
                case 'outComesErrorMessageType':
                case 'outComesErrorStatus':
                case 'outComesErrorCourseId':
                case 'outComesErrorError':
                case 'outComesErrorTransactionId':
                case 'outcomesErrorFromDate':
                case 'outcomesErrorToDate':
                    if ((typeof value === "string") && value !== '') {
                        search = search.concat(key,':',value,'%2C')
                    }
                    break;
                default:
            }
        }

        // remove the last %2C and add to the url
        if (search.length === 11) {
            search = '';
        } else if (search.length > 10) {
            search = search.slice(0, -3).concat('%7D')
        } else {
            search = '';
        }

        // publish the url to the table component
        EventEmitter.emit('tableFilterUrl', search)
        EventEmitter.emit('openNav', '')
    };

    clearFilteredResults = () => {
        const newState = Object.assign({}, this.state);
        newState.outcomesErrorFromDate = '';
        newState.outcomesErrorToDate = '';

        newState.outComesErrorPartnerId = '';
        newState.outComesErrorPartnerName = '';
        newState.outComesErrorInstitutionId = '';
        newState.outComesErrorInstitutionName = '';
        newState.outComesErrorDataSource = '';
        newState.outComesErrorMessageType = '';
        newState.outComesErrorStatus = '';
        newState.outComesErrorCourseId = '';
        newState.outComesErrorError = '';
        newState.outComesErrorTransactionId = '';

        newState.errors = {};

        this.setState(newState);

        EventEmitter.emit('tableFilterUrl', '')
        EventEmitter.emit('openNav', '')
    };

    /**
     * fire alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    render() {
        return(
            <div className="view-error-filter-container">
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
                <div className="view-audit-filter-button-container">
                    <input type="button" className="view-audit-filter-cancel" value="Clear"
                           onClick={() => this.clearFilteredResults()} />
                    <input type="button" className="view-audit-filter-submit" value="Filter"
                           onClick={() => this.loadFilteredResults()}/>
                </div>
            </div>
        )
    }
}

export default ViewErrorFilterForm;
